import useBrowserDate from '../../hooks/useBrowserDate';
import { getCalendarDays } from '../../utils/calendarUtils';

const CALENDAR = { left: 380, top: 30, width: 76, height: 76 };
const HEADER_HEIGHT = 17;
const GRID_COLUMNS = 7;
const CELL_WIDTH = 9.5;
const CELL_HEIGHT = 8.5;
const GRID_LEFT = CALENDAR.left + (CALENDAR.width - GRID_COLUMNS * CELL_WIDTH) / 2;
const GRID_TOP = CALENDAR.top + HEADER_HEIGHT + 6;
const RING_XS = [396, 418, 440];
const NAIL = { x: CALENDAR.left + CALENDAR.width / 2, y: 14 };

/**
 * A paper calendar hanging from a nail on the lab wall, showing this month with today circled.
 * The month and days are filled in once the page runs in the browser; pre-rendered pages show a blank page.
 */
export default function WallCalendar({ date: fixedDate }) {
  const browserDate = useBrowserDate();
  const date = fixedDate ?? browserDate;
  const monthLabel = date ? date.toLocaleString('en-US', { month: 'long' }).toUpperCase() : '';
  const days = date ? getCalendarDays(date.getFullYear(), date.getMonth()) : [];
  const today = date?.getDate();

  return (
    <g aria-hidden="true">
      {/* nail and hanging string */}
      <path
        d={`M${RING_XS[0]} ${CALENDAR.top} L${NAIL.x} ${NAIL.y} L${RING_XS[2]} ${CALENDAR.top}`}
        stroke="#8a6a3f"
        strokeWidth="1.2"
        fill="none"
      />
      <circle cx={NAIL.x} cy={NAIL.y} r="2.5" fill="#6b7a8f" />

      {/* page */}
      <rect x={CALENDAR.left + 2} y={CALENDAR.top + 2} width={CALENDAR.width} height={CALENDAR.height} rx="3" fill="#cfd9ec" />
      <rect x={CALENDAR.left} y={CALENDAR.top} width={CALENDAR.width} height={CALENDAR.height} rx="3" fill="#ffffff" stroke="#c9d3e6" strokeWidth="1" />
      <path
        d={`M${CALENDAR.left} ${CALENDAR.top + HEADER_HEIGHT} V${CALENDAR.top + 3} q0 -3 3 -3 H${CALENDAR.left + CALENDAR.width - 3} q3 0 3 3 V${CALENDAR.top + HEADER_HEIGHT} Z`}
        fill="#ff7a59"
      />
      <text
        x={CALENDAR.left + CALENDAR.width / 2}
        y={CALENDAR.top + 12.5}
        textAnchor="middle"
        fontSize="8.5"
        fontWeight="700"
        fill="#ffffff"
        letterSpacing="0.5"
      >
        {monthLabel}
      </text>

      {/* spiral rings */}
      {RING_XS.map((x) => (
        <rect key={x} x={x - 1.5} y={CALENDAR.top - 4} width="3" height="8" rx="1.5" fill="#6b7a8f" />
      ))}

      {/* day numbers */}
      {days.map(({ day, column, row }) => {
        const cellCenterX = GRID_LEFT + column * CELL_WIDTH + CELL_WIDTH / 2;
        const cellCenterY = GRID_TOP + row * CELL_HEIGHT + CELL_HEIGHT / 2;
        const isToday = day === today;
        return (
          <g key={day}>
            {isToday && <circle cx={cellCenterX} cy={cellCenterY - 1} r="4.3" fill="none" stroke="#ff7a59" strokeWidth="1.2" />}
            <text
              x={cellCenterX}
              y={cellCenterY + 1.5}
              textAnchor="middle"
              fontSize="5"
              fontWeight={isToday ? '700' : '400'}
              fill={column === 0 ? '#e0605a' : '#5b6b80'}
            >
              {day}
            </text>
          </g>
        );
      })}
    </g>
  );
}
